import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LayoutDashboard, FolderKanban, LogOut, Shield, CheckCircle, CalendarDays, CalendarOff, Activity, Archive, Bookmark, Menu, X, BarChart3, Settings, Bell, Timer } from 'lucide-react';

import { useAuthStore } from '@/stores/auth.store';
import { useWorkspaceStore } from '@/stores/workspace.store';
import { Avatar } from '@/components/ui/Avatar';
import { Logo } from '@/components/ui/Logo';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { NotificationBell } from '@/components/ui/NotificationBell';
import { api } from '@/utils/api';

interface ProjectProgress {
  id: number;
  name: string;
  done: number;
  total: number;
}

export function Sidebar() {
  const { user, logout } = useAuthStore();
  const { workspaces, fetchWorkspaces } = useWorkspaceStore();
  const params = useParams();
  const currentWsId = params.workspaceId ? parseInt(params.workspaceId) : null;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [projectProgress, setProjectProgress] = useState<Record<number, ProjectProgress[]>>({});

  useEffect(() => { fetchWorkspaces(); }, [fetchWorkspaces]);

  // Fetch project progress per workspace (single API call each)
  useEffect(() => {
    const fetchProgress = async () => {
      const progress: Record<number, ProjectProgress[]> = {};
      await Promise.all(workspaces.map(async (ws) => {
        try {
          const data = await api.get<{ projects: ProjectProgress[] }>(`/workspaces/${ws.id}/project-progress`);
          progress[ws.id] = data.projects;
        } catch { /* skip */ }
      }));
      setProjectProgress(progress);
    };
    if (workspaces.length > 0) fetchProgress();
  }, [workspaces]);

  const sidebarContent = (
    <>
      <div className="p-4 border-b border-default flex items-center justify-between">
        <Link to="/dashboard"><Logo /></Link>
        <button className="md:hidden text-muted hover:text-default" onClick={() => setMobileOpen(false)}>
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted hover:text-default hover:bg-hover transition-colors">
          <LayoutDashboard className="w-4 h-4" />
          Dashboard
        </Link>
        <Link to="/my-day" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted hover:text-default hover:bg-hover transition-colors">
          <CalendarDays className="w-4 h-4" />
          Mein Tag
        </Link>
        <Link to="/my-tasks" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted hover:text-default hover:bg-hover transition-colors">
          <CheckCircle className="w-4 h-4" />
          Meine Aufgaben
        </Link>
        <Link to="/bookmarks" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted hover:text-default hover:bg-hover transition-colors">
          <Bookmark className="w-4 h-4" />
          Lesezeichen
        </Link>
        <Link to="/time-tracking" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted hover:text-default hover:bg-hover transition-colors">
          <Timer className="w-4 h-4" />
          Zeiterfassung
        </Link>
        <Link to="/gf-view" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted hover:text-default hover:bg-hover transition-colors">
          <BarChart3 className="w-4 h-4" />
          GF-View
        </Link>
        <Link to="/notifications" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted hover:text-default hover:bg-hover transition-colors">
          <Bell className="w-4 h-4" />
          Benachrichtigungen
        </Link>

        <div className="mt-4 pt-4 border-t border-default/40 px-3 pb-2 text-xs font-semibold text-dim uppercase tracking-wider">Workspaces</div>
        {workspaces.map((ws) => (
          <div key={ws.id}>
            <Link
              to={`/w/${ws.id}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${currentWsId === ws.id ? 'bg-accent/10 text-accent font-medium border-l-2 border-accent' : 'text-muted hover:text-default hover:bg-hover'}`}
            >
              <FolderKanban className="w-4 h-4" />
              <span className="truncate flex-1">{ws.name}</span>
            </Link>

            {/* Project Progress Bars */}
            {currentWsId === ws.id && projectProgress[ws.id]?.map(proj => (
              <Link
                key={proj.id}
                to={`/w/${ws.id}/p/${proj.id}`}
                className="flex items-center gap-2 px-3 py-1 ml-4 text-xs text-dim hover:text-muted transition-colors"
              >
                <span className="truncate flex-1">{proj.name}</span>
                {proj.total > 0 && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <div className="w-12 h-1.5 bg-hover rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent rounded-full transition-all"
                        style={{ width: `${(proj.done / proj.total) * 100}%` }}
                      />
                    </div>
                    <span className="text-[10px] w-6 text-right">{Math.round((proj.done / proj.total) * 100)}%</span>
                  </div>
                )}
              </Link>
            ))}

            {/* Workspace Sub-Links */}
            {currentWsId === ws.id && (
              <div className="ml-4 space-y-0.5 mt-1">
                <Link
                  to={`/w/${ws.id}/activity`}
                  className="flex items-center gap-2 px-3 py-1 rounded text-xs text-dim hover:text-muted transition-colors"
                >
                  <Activity className="w-3 h-3" /> Aktivitäten
                </Link>
                <Link
                  to={`/w/${ws.id}/archive`}
                  className="flex items-center gap-2 px-3 py-1 rounded text-xs text-dim hover:text-muted transition-colors"
                >
                  <Archive className="w-3 h-3" /> Archiv
                </Link>
                <Link
                  to={`/w/${ws.id}/absences`}
                  className="flex items-center gap-2 px-3 py-1 rounded text-xs text-dim hover:text-muted transition-colors"
                >
                  <CalendarOff className="w-3 h-3" /> Abwesenheiten
                </Link>
              </div>
            )}
          </div>
        ))}
      </nav>

      <div className="p-4 pb-20 md:pb-4 border-t border-default space-y-2">
        <Link to="/settings" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted hover:text-default hover:bg-hover transition-colors">
            <Settings className="w-4 h-4" />
            Einstellungen
          </Link>
        {user?.is_admin === 1 && (
          <Link to="/admin" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted hover:text-default hover:bg-hover transition-colors">
            <Shield className="w-4 h-4" />
            Admin
          </Link>
        )}
        <div className="flex items-center gap-2 px-3 py-2.5 bg-hover/50 rounded-lg">
          <Avatar name={user?.display_name || ''} size="sm" />
          <span className="text-sm truncate flex-1">{user?.display_name}</span>
          <NotificationBell />
          <ThemeToggle />
          <button onClick={logout} className="text-muted hover:text-default transition-colors" title="Logout" aria-label="Abmelden">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile top bar with hamburger + logo */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-card border-b border-default flex items-center gap-2 px-3">
        <button
          className="p-2 text-muted hover:text-default transition-colors"
          onClick={() => setMobileOpen(true)}
          aria-label="Menü öffnen"
        >
          <Menu className="w-5 h-5" />
        </button>
        <Link to="/dashboard"><Logo /></Link>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        w-60 bg-card border-r border-default flex flex-col h-screen shrink-0
        max-md:fixed max-md:z-50 max-md:transition-transform max-md:duration-200
        ${mobileOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full'}
      `}>
        {sidebarContent}
      </aside>
    </>
  );
}
